import { assertOk, readSSE, safeJson } from "../stream";
import type {
  ChatMessage,
  ChatRequest,
  CompletionResult,
  LLMProvider,
  StreamChunk,
  ToolCall,
} from "../types";

export interface OpenAIOptions {
  baseUrl: string;
  apiKey?: string;
  model: string;
  maxTokens: number;
  organization?: string;
  /** Header carrying the key. Default "Authorization". */
  authHeader?: string;
  /** Scheme prefix. Default "Bearer". */
  authScheme?: string;
  extraHeaders?: Record<string, string>;
}

interface OpenAIToolCall {
  id?: string;
  function?: { name?: string; arguments?: string };
}

/** OpenAI Chat Completions API (and any OpenAI-compatible endpoint). */
export class OpenAIProvider implements LLMProvider {
  readonly kind = "openai" as const;
  readonly supportsTools = true;

  constructor(private readonly opts: OpenAIOptions) {}

  private headers(): Record<string, string> {
    const h: Record<string, string> = {
      "content-type": "application/json",
      ...this.opts.extraHeaders,
    };
    if (this.opts.apiKey) {
      const name = this.opts.authHeader ?? "Authorization";
      const scheme = this.opts.authScheme ?? "Bearer";
      h[name] = scheme ? `${scheme} ${this.opts.apiKey}` : this.opts.apiKey;
    }
    if (this.opts.organization) h["OpenAI-Organization"] = this.opts.organization;
    return h;
  }

  private endpoint(): string {
    return `${this.opts.baseUrl.replace(/\/$/, "")}/chat/completions`;
  }

  private body(req: ChatRequest, stream: boolean): Record<string, unknown> {
    const body: Record<string, unknown> = {
      model: req.model,
      max_tokens: req.maxTokens,
      stream,
      messages: toOpenAIMessages(req),
    };
    if (req.tools?.length) {
      body.tools = req.tools.map((t) => ({
        type: "function",
        function: {
          name: t.name,
          description: t.description,
          parameters: t.parameters,
        },
      }));
      body.tool_choice = "auto";
    }
    return body;
  }

  async *stream(
    req: ChatRequest,
    signal?: AbortSignal,
  ): AsyncGenerator<StreamChunk> {
    const res = await assertOk(
      await fetch(this.endpoint(), {
        method: "POST",
        headers: this.headers(),
        body: JSON.stringify(this.body(req, true)),
        signal,
      }),
    );

    let finishReason: string | undefined;
    for await (const data of readSSE(res)) {
      if (data === "[DONE]") break;
      const evt = JSON.parse(data) as {
        choices?: { delta?: { content?: string }; finish_reason?: string }[];
      };
      const choice = evt.choices?.[0];
      if (choice?.delta?.content) {
        yield { type: "text", text: choice.delta.content };
      }
      if (choice?.finish_reason) finishReason = choice.finish_reason;
    }
    yield { type: "done", finishReason };
  }

  async complete(
    req: ChatRequest,
    signal?: AbortSignal,
  ): Promise<CompletionResult> {
    const res = await assertOk(
      await fetch(this.endpoint(), {
        method: "POST",
        headers: this.headers(),
        body: JSON.stringify(this.body(req, false)),
        signal,
      }),
    );
    const json = (await res.json()) as {
      choices?: {
        message?: { content?: string | null; tool_calls?: OpenAIToolCall[] };
        finish_reason?: string;
      }[];
    };
    const choice = json.choices?.[0];
    const toolCalls: ToolCall[] = (choice?.message?.tool_calls ?? []).map(
      (tc) => ({
        id: tc.id ?? "call_" + Math.random().toString(36).slice(2, 12),
        name: tc.function?.name ?? "",
        arguments: safeJson(tc.function?.arguments ?? ""),
      }),
    );
    return {
      text: choice?.message?.content ?? "",
      toolCalls,
      finishReason: choice?.finish_reason,
    };
  }
}

function toOpenAIMessages(req: ChatRequest): unknown[] {
  const out: unknown[] = [];
  if (req.system) out.push({ role: "system", content: req.system });
  for (const m of req.messages) {
    out.push(mapMessage(m));
  }
  return out;
}

function mapMessage(m: ChatMessage): unknown {
  if (m.role === "tool") {
    return { role: "tool", tool_call_id: m.toolCallId, content: m.content };
  }
  if (m.role === "assistant" && m.toolCalls?.length) {
    return {
      role: "assistant",
      content: m.content || null,
      tool_calls: m.toolCalls.map((tc) => ({
        id: tc.id,
        type: "function",
        function: { name: tc.name, arguments: JSON.stringify(tc.arguments) },
      })),
    };
  }
  return { role: m.role, content: m.content };
}
